// File completion of one batch (TSD §5): what a dropped file is, which row and
// which of its files it belongs to, and what is wrong with it. Pure (no React,
// no request), unit-tested.
//
// The backend decides every row status, required file and missing file; this
// module reads them from the rows API (files[kind].status) and never derives
// them. The few backend rules it mirrors (file types and sizes, the NIK in a
// file name) are only there to keep a file the backend would refuse from
// being uploaded at all: the stage endpoint checks those two after the bytes
// reached media-service, so a refused upload leaves an orphan media object.
import { isJunkFile } from '@src/services/droppedFiles'
import { STAGE_LIMITS } from './constants'

// enquiry_import.validation FILE_KINDS, in the same order.
export const FILE_KINDS = ['ktp_photo', 'terms_condition', 'job_application_form']
export const DOCUMENT_KINDS = ['terms_condition', 'job_application_form']

// Words in a document's file name that tell its kind, anywhere in the name,
// any case. The template's Petunjuk sheet lists the same ones.
const KIND_KEYWORDS = {
  terms_condition: ['tnc', 't&c', 'syarat', 'terms'],
  job_application_form: ['lamaran', 'application'],
}

const PHOTO_MIMES = new Set(Object.values(STAGE_LIMITS.photo.types))

// An email address inside a file name ("budi@contoh.com_tnc.pdf").
const EMAIL = '[a-z0-9._%+-]+@[a-z0-9-]+(?:\\.[a-z0-9-]+)+'
const EMAILS_IN_NAME = new RegExp(EMAIL, 'gi')
const HAS_EMAIL = new RegExp(EMAIL, 'i')

let lastEntryId = 0
/** A new id for a file added in this browser (tray, pending, upload queue). */
export function nextEntryId() {
  lastEntryId += 1
  return `file-${lastEntryId}`
}

function extensionOf(name) {
  const m = /\.([a-z0-9]{1,10})$/i.exec(String(name || ''))
  return m ? m[1].toLowerCase() : ''
}

/**
 * The name without its extension and without a trailing copy counter
 * ("3171…0001_tnc (1).pdf" -> "3171…0001_tnc"), lower case: what keys and
 * keywords are looked for in.
 */
export function baseName(name) {
  return String(name || '')
    .replace(/\.[a-z0-9]{1,10}$/i, '')
    .replace(/\s*\(\d+\)$/, '')
    .trim()
    .toLowerCase()
}

/** The kinds HR supplies in this batch, from the detail's required_files. */
export function batchFileKinds(requiredFiles) {
  return FILE_KINDS.filter((kind) => Boolean(requiredFiles?.[kind]))
}

/**
 * Photo or document, by extension, with the type and size limits of its
 * uploader. `{ family, typeFix }` or `{ reject, family? }`; reject is one of
 * junk | heic | unsupported_type | empty | too_large.
 *
 * typeFix: the backend's KTP uploader checks the declared MIME type only, and a
 * browser may declare none (or a variant such as image/jpg) for a real JPG.
 * Such a photo is sent with the type of its extension, the one the backend
 * stores anyway (STAGED_MIME_BY_EXT). Documents pass on their extension alone.
 */
export function fileFamily(file) {
  const name = file?.name || ''
  if (isJunkFile(name)) return { reject: 'junk' }
  const ext = extensionOf(name)
  if (ext === 'heic' || ext === 'heif') return { reject: 'heic', family: 'photo' }
  const photoType = STAGE_LIMITS.photo.types[ext]
  const family = photoType ? 'photo' : STAGE_LIMITS.document.types[ext] ? 'document' : null
  if (!family) return { reject: 'unsupported_type' }
  if (!file.size) return { reject: 'empty', family }
  if (file.size > STAGE_LIMITS[family].maxBytes) return { reject: 'too_large', family }
  const typeFix = family === 'photo' && !PHOTO_MIMES.has(file.type) ? photoType : null
  return { family, typeFix }
}

/** The document kinds named by keywords in a file name (an email in it is ignored). */
export function keywordKinds(name) {
  const text = baseName(name).replace(EMAILS_IN_NAME, ' ')
  return DOCUMENT_KINDS.filter((kind) => KIND_KEYWORDS[kind].some((word) => text.includes(word)))
}

/**
 * What a dropped file is for this batch (batchKinds: batchFileKinds).
 *   photo -> ktp_photo, unless its name has a document keyword: a photo named
 *     as a T&C or a letter ("<NIK>_tnc.jpg", a phone shot of a signed page) is
 *     never taken as the row's KTP photo; it is refused (wrong_type: a document
 *     must be a PDF, DOC or DOCX; kind_not_needed when the batch takes no such
 *     document)
 *   document -> the kind its keyword names; no keyword: the batch's only
 *     document kind, or docKindUnknown (the row decides, else HR chooses);
 *     keywords of both kinds: docKindUnknown
 * `{ family, kind, docKindUnknown, typeFix }` or `{ reject, family?, kind? }`:
 * fileFamily's rejects, plus kind_not_needed (the batch needs no file of that
 * kind: a photo where candidates upload their own e-KTP, a letter in a batch
 * that only takes T&C) and wrong_type.
 */
export function classifyFile(file, batchKinds) {
  const f = fileFamily(file)
  if (f.reject) return f
  if (f.family === 'photo') {
    const named = keywordKinds(file.name)
    if (named.length === 1 && !batchKinds.includes(named[0])) {
      return { reject: 'kind_not_needed', family: 'photo', kind: named[0] }
    }
    if (named.length) return { reject: 'wrong_type', family: 'photo', kind: named.length === 1 ? named[0] : null }
    if (!batchKinds.includes('ktp_photo')) return { reject: 'kind_not_needed', family: 'photo', kind: 'ktp_photo' }
    return { family: 'photo', kind: 'ktp_photo', docKindUnknown: false, typeFix: f.typeFix }
  }
  const batchDocKinds = DOCUMENT_KINDS.filter((kind) => batchKinds.includes(kind))
  const named = keywordKinds(file.name)
  if (named.length === 1) {
    if (!batchDocKinds.includes(named[0])) return { reject: 'kind_not_needed', family: 'document', kind: named[0] }
    return { family: 'document', kind: named[0], docKindUnknown: false, typeFix: null }
  }
  if (!batchDocKinds.length) return { reject: 'kind_not_needed', family: 'document', kind: null }
  if (!named.length && batchDocKinds.length === 1) {
    return { family: 'document', kind: batchDocKinds[0], docKindUnknown: false, typeFix: null }
  }
  return { family: 'document', kind: null, docKindUnknown: true, typeFix: null }
}

/**
 * A file HR puts on one row's file explicitly (a cell's "Choose file", the
 * assign dialog): only its type and size count, not its name's keywords.
 * `{ typeFix }` or `{ reject }` (fileFamily's, or wrong_type).
 */
export function checkFileFor(file, kind) {
  const f = fileFamily(file)
  if (f.reject) return f
  if (f.family !== (kind === 'ktp_photo' ? 'photo' : 'document')) return { reject: 'wrong_type', family: f.family, kind }
  return { typeFix: f.typeFix }
}

/** The distinct 16-digit runs of a file name (a run of 17 digits is none). */
export function nikRuns(name) {
  const runs = (String(name || '').match(/\d+/g) || []).filter((run) => run.length === 16)
  return [...new Set(runs)]
}

/**
 * Whether a file name names another person than the row: it carries 16-digit
 * runs and none of them is the row's NIK. Mirror of enquiry_import.validation
 * fileNikMismatch (the stage answers 409 file_nik_mismatch); a name without a
 * run, or a row without a NIK, is not checked.
 */
export function fileNikMismatch(fileName, rowNik) {
  const nik = String(rowNik || '')
  if (!/^\d{16}$/.test(nik)) return false
  const runs = nikRuns(fileName)
  return runs.length > 0 && !runs.includes(nik)
}

/**
 * What a row still takes of `kind`: its files[kind].status as the rows API
 * sent it ('missing' | 'staged' | 'reused'), null when the row does not need
 * that kind (not_required, leftover included).
 */
export function rowNeeds(row, kind) {
  const status = row?.files?.[kind]?.status
  return status && status !== 'not_required' ? status : null
}

/**
 * What one cell of the file grid shows for a row and a kind:
 *   'none'      the row does not take that kind
 *   'leftover'  a saved file the row no longer needs (settings re-applied)
 *   'pending'   a file added in this browser, not saved yet (`entry`)
 *   'staged' | 'reused' | 'missing'  files[kind].status as the API sent it
 */
export function cellState(row, kind, entry = null) {
  const file = row?.files?.[kind]
  if (!file || file.status === 'not_required') return file?.leftover ? 'leftover' : 'none'
  if (entry) return 'pending'
  return file.status
}

/** The kinds of `family` a file may be assigned to on this row: needed, and not a reused e-KTP. */
export function assignableKinds(row, family) {
  const kinds = family === 'photo' ? ['ktp_photo'] : DOCUMENT_KINDS
  return kinds.filter((kind) => {
    const need = rowNeeds(row, kind)
    return need === 'missing' || need === 'staged'
  })
}

/**
 * What names the row in a file name: the NIK when HR supplies the e-KTP
 * (needs_hr_ktp), else the email or Employee ID (OQ-20: those batches keep no
 * NIK).
 */
export function keyMode(batch) {
  return batch?.needs_hr_ktp === false ? 'email' : 'nik'
}

const lower = (value) => (value ? String(value).trim().toLowerCase() : '')

/** Lookup tables of the rows waiting for files, built once per drop. */
export function buildKeyIndex(targets, mode) {
  if (mode === 'nik') {
    const byNik = new Map()
    for (const row of targets || []) {
      const nik = String(row?.nik || '')
      if (!/^\d{16}$/.test(nik)) continue
      if (!byNik.has(nik)) byNik.set(nik, [])
      byNik.get(nik).push(row.id)
    }
    return { mode, byNik }
  }
  const keys = []
  for (const row of targets || []) {
    const emails = [...new Set([lower(row?.email), lower(row?.invite_email)].filter(Boolean))]
    emails.forEach((key) => keys.push({ key, rowId: row.id, type: 'email' }))
    const code = lower(row?.employee_code)
    if (code) keys.push({ key: code, rowId: row.id, type: 'code' })
  }
  return { mode, keys }
}

const EMAIL_LOCAL_CHAR = /[a-z0-9._%+-]/
const EMAIL_DOMAIN_CHAR = /[a-z0-9-]/
const ALNUM = /[a-z0-9]/

// Whether `key` at `at` in `text` stands on its own: an email is not part of a
// longer local part or domain ("xbudi@…", "tnc_budi@…", "…@contoh.company",
// "…@contoh.com.au"; a file meant for someone outside the batch must not land
// on a row whose address it merely contains); an Employee ID is not part of a
// longer code ("ID-1" in "ID-10").
function bounded(text, at, key, type) {
  const before = at > 0 ? text[at - 1] : ''
  const after = text[at + key.length] || ''
  if (type === 'email') {
    if (before && EMAIL_LOCAL_CHAR.test(before)) return false
    if (after && EMAIL_DOMAIN_CHAR.test(after)) return false
    if (after === '.' && ALNUM.test(text[at + key.length + 1] || '')) return false
    return true
  }
  return !(before && ALNUM.test(before)) && !(after && ALNUM.test(after))
}

const KEYWORDS = Object.values(KIND_KEYWORDS).flat()
const LEFT_WORD = /(?:^|[^a-z0-9&])([a-z0-9&]+)[._%+-]$/
const RIGHT_WORD = /^[.-]([a-z0-9&]+)(?:$|[^a-z0-9&])/

// Whether the email `key` at `at` runs on into a document keyword, glued by
// one separator: "tnc_budi@…", "tnc-budi@…", "…@contoh.com-tnc",
// "…@contoh.com.tnc". Not a match (it could be another address), but most
// likely a name meant for that row: the reason says so and suggests it.
function keywordJoined(text, at, key) {
  const left = LEFT_WORD.exec(text.slice(0, at))
  const right = RIGHT_WORD.exec(text.slice(at + key.length))
  return Boolean((left && KEYWORDS.includes(left[1])) || (right && KEYWORDS.includes(right[1])))
}

/**
 * The rows a file name points at. `{ rowIds, reason }`, reason null when it is
 * exactly one row, else:
 *   nik mode    no_key (no 16-digit run), multiple_niks (two different runs),
 *               unknown_key (no row waiting for files has it), ambiguous_key
 *   email mode  no_key / unknown_key (the name has an email no row has),
 *               ambiguous_key (two rows' keys in one name), email_joined (a
 *               row's email runs on into a document keyword, "tnc_budi@x.com"
 *               or "budi@x.com-tnc": not taken as that email, since it may
 *               be another address; rowIds = those rows, suggested to HR)
 * In email mode a key found inside a longer key of another row (an Employee
 * ID "budi" inside the email "budi@x.com") gives way to it.
 */
export function findRows(fileName, index) {
  const text = baseName(fileName)
  if (index.mode === 'nik') {
    const runs = nikRuns(text)
    if (!runs.length) return { rowIds: [], reason: 'no_key' }
    if (runs.length > 1) return { rowIds: [], reason: 'multiple_niks' }
    const rowIds = index.byNik.get(runs[0]) || []
    if (!rowIds.length) return { rowIds, reason: 'unknown_key' }
    return { rowIds, reason: rowIds.length > 1 ? 'ambiguous_key' : null }
  }

  const hits = []
  const joined = []
  for (const { key, rowId, type } of index.keys) {
    let at = text.indexOf(key)
    while (at >= 0) {
      if (bounded(text, at, key, type)) hits.push({ rowId, start: at, end: at + key.length })
      else if (type === 'email' && keywordJoined(text, at, key)) joined.push(rowId)
      at = text.indexOf(key, at + 1)
    }
  }
  const kept = hits.filter(
    (hit) =>
      !hits.some(
        (other) =>
          other.rowId !== hit.rowId &&
          other.start <= hit.start &&
          other.end >= hit.end &&
          other.end - other.start > hit.end - hit.start
      )
  )
  const rowIds = [...new Set(kept.map((hit) => hit.rowId))]
  if (!rowIds.length) {
    if (joined.length) return { rowIds: [...new Set(joined)], reason: 'email_joined' }
    return { rowIds, reason: HAS_EMAIL.test(text) ? 'unknown_key' : 'no_key' }
  }
  return { rowIds, reason: rowIds.length > 1 ? 'ambiguous_key' : null }
}

/** Same name and size as the row's saved file of that kind: most likely that very file. */
function sameAsSaved(file, saved) {
  return Boolean(saved?.file_name) && saved.file_name === file.name && Number(saved.file_size) === file.size
}

/**
 * Sort one drop (or pick) into:
 *   matched    [{ id, file, rowId, rowNo, kind, family, typeFix, replaces, alreadySaved }]
 *              replaces: the row already has a saved file of that kind;
 *              alreadySaved: same name and size as that saved file (the
 *              caller leaves it out of Save unless HR asks)
 *   unmatched  [{ id, file, reason, family, kind, typeFix, rowIds }] (HR assigns)
 *              the findRows reasons, plus choose_kind (a document whose kind
 *              the row cannot tell), not_needed_for_row (a letter for an
 *              employee), ktp_reused (the row reuses the e-KTP on file) and
 *              duplicate_target (two files of this drop for one row + kind:
 *              neither is picked)
 *   rejected   [{ id, file, reason, family, kind }] (classifyFile's rejects)
 * Junk (.DS_Store, ...) is dropped silently.
 *
 * @param {File[]} files
 * @param {object[]} targets  the rows waiting for files (GET rows, incomplete + ready)
 * @param {{ keyMode: 'nik'|'email', batchKinds: string[] }} opts
 */
export function matchFiles(files, targets, { keyMode: mode, batchKinds }) {
  const index = buildKeyIndex(targets, mode)
  const rowsById = new Map((targets || []).map((row) => [row.id, row]))
  const matched = []
  const unmatched = []
  const rejected = []
  const candidates = []

  for (const file of files || []) {
    const c = classifyFile(file, batchKinds)
    if (c.reject === 'junk') continue
    const id = nextEntryId()
    if (c.reject) {
      rejected.push({ id, file, reason: c.reject, family: c.family || null, kind: c.kind || null })
      continue
    }
    const base = { id, file, family: c.family, kind: c.kind, typeFix: c.typeFix }
    const found = findRows(file.name, index)
    if (found.reason) {
      unmatched.push({ ...base, reason: found.reason, rowIds: found.rowIds })
      continue
    }
    const row = rowsById.get(found.rowIds[0])
    let { kind } = c
    if (!kind) {
      const needed = DOCUMENT_KINDS.filter((k) => batchKinds.includes(k) && rowNeeds(row, k))
      if (needed.length !== 1) {
        unmatched.push({ ...base, reason: needed.length ? 'choose_kind' : 'not_needed_for_row', rowIds: [row.id] })
        continue
      }
      ;[kind] = needed
    }
    const need = rowNeeds(row, kind)
    if (!need || need === 'reused') {
      unmatched.push({ ...base, kind, reason: need ? 'ktp_reused' : 'not_needed_for_row', rowIds: [row.id] })
      continue
    }
    candidates.push({
      ...base,
      kind,
      rowId: row.id,
      rowNo: row.row_no,
      replaces: need === 'staged',
      alreadySaved: need === 'staged' && sameAsSaved(file, row.files[kind]),
    })
  }

  const byTarget = new Map()
  for (const entry of candidates) {
    const key = pendingKey(entry.rowId, entry.kind)
    if (!byTarget.has(key)) byTarget.set(key, [])
    byTarget.get(key).push(entry)
  }
  for (const group of byTarget.values()) {
    if (group.length === 1) matched.push(group[0])
    else
      group.forEach(({ id, file, family, kind, typeFix, rowId }) =>
        unmatched.push({ id, file, family, kind, typeFix, reason: 'duplicate_target', rowIds: [rowId] })
      )
  }
  return { matched, unmatched, rejected }
}

/** Key of a row's file of one kind: one file per key waits to be saved. */
export function pendingKey(rowId, kind) {
  return `${rowId}:${kind}`
}

/** The same file picked twice (same name, size and modification time). */
export function sameFile(a, b) {
  return Boolean(a && b) && a.name === b.name && a.size === b.size && a.lastModified === b.lastModified
}

/**
 * Add matched / assigned files to the files waiting to be saved (a Map of
 * pendingKey -> entry). A newer file for the same row + kind replaces the one
 * waiting (listed in `replaced`); the very same file again is ignored
 * (`same`).
 * @returns {{ pending: Map, replaced: object[], same: object[] }}
 */
export function mergePending(pending, incoming) {
  const next = new Map(pending)
  const replaced = []
  const same = []
  for (const entry of incoming || []) {
    const key = pendingKey(entry.rowId, entry.kind)
    const current = next.get(key)
    if (current && sameFile(current.file, entry.file)) {
      same.push(entry)
      continue
    }
    if (current) replaced.push(current)
    next.set(key, entry)
  }
  return { pending: next, replaced, same }
}

/**
 * The waiting files against a fresh read of the rows: an entry whose row is no
 * longer waiting for files, or no longer takes that kind (settings re-applied,
 * row removed or processed), is dropped (listed in `dropped`); the others get
 * the row's current number and `replaces`.
 * @returns {{ pending: Map, dropped: object[] }}
 */
export function reconcilePending(pending, targets) {
  const rowsById = new Map((targets || []).map((row) => [row.id, row]))
  const next = new Map()
  const dropped = []
  for (const [key, entry] of pending) {
    const row = rowsById.get(entry.rowId)
    const need = row ? rowNeeds(row, entry.kind) : null
    if (!need || need === 'reused') {
      dropped.push(entry)
      continue
    }
    next.set(key, { ...entry, rowNo: row.row_no, replaces: need === 'staged' })
  }
  return { pending: next, dropped }
}

/**
 * SHA-256 of a file, hex; null when the browser offers no WebCrypto (a page not
 * served over https or localhost) or the file cannot be read: the duplicate
 * warnings are then skipped (the backend still refuses a KTP photo staged on
 * two rows).
 */
export async function hashFile(file, subtle = globalThis.crypto?.subtle) {
  if (!subtle || typeof file?.arrayBuffer !== 'function') return null
  try {
    const digest = await subtle.digest('SHA-256', await file.arrayBuffer())
    return Array.from(new Uint8Array(digest), (byte) => byte.toString(16).padStart(2, '0')).join('')
  } catch {
    return null
  }
}

/**
 * Hash many files, `concurrency` at a time (reading a file is the slow part).
 * @returns {Promise<string[]>} one hash (or null) per file, same order
 */
export async function hashFiles(files, { concurrency = 2, onProgress, subtle } = {}) {
  const list = Array.from(files || [])
  const out = new Array(list.length).fill(null)
  let next = 0
  let done = 0
  const worker = async () => {
    while (next < list.length) {
      const i = next
      next += 1
      // eslint-disable-next-line no-await-in-loop
      out[i] = await hashFile(list[i], subtle)
      done += 1
      onProgress?.(done, list.length)
    }
  }
  await Promise.all(Array.from({ length: Math.min(concurrency, list.length) }, worker))
  return out
}

/**
 * Identical content among the files waiting to be saved
 * ([{ id, rowId, rowNo, kind, sha256 }]). Map id -> { blocking, rowNos, sameRow }:
 *   an e-KTP photo on two rows       blocking (the backend refuses the second
 *                                    one, 409 ktp_photo_in_use: a photo is one
 *                                    person's)
 *   a document on two rows           warning only (OQ-21: one generic T&C may
 *                                    serve many rows)
 *   a row's T&C = its letter         warning (sameRow)
 * rowNos: the OTHER rows with that content.
 */
export function duplicateContent(entries) {
  const byHash = new Map()
  for (const entry of entries || []) {
    if (!entry?.sha256) continue
    if (!byHash.has(entry.sha256)) byHash.set(entry.sha256, [])
    byHash.get(entry.sha256).push(entry)
  }
  const out = new Map()
  for (const group of byHash.values()) {
    if (group.length < 2) continue
    for (const entry of group) {
      const others = group.filter((other) => other !== entry)
      const otherRows = [...new Set(others.filter((o) => o.rowId !== entry.rowId).map((o) => o.rowNo))].sort(
        (a, b) => a - b
      )
      if (entry.kind === 'ktp_photo') {
        if (otherRows.length) out.set(entry.id, { blocking: true, rowNos: otherRows, sameRow: false })
        continue
      }
      const sameRow = others.some((o) => o.rowId === entry.rowId)
      if (otherRows.length || sameRow) out.set(entry.id, { blocking: false, rowNos: otherRows, sameRow })
    }
  }
  return out
}
